import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Boxes, Plus } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { EmptyState } from '@/components/EmptyState';
import { MoneyAmount } from '@/components/MoneyAmount';
import { Pagination } from '@/components/Pagination';
import { BatonWarnings } from '@/components/BatonWarnings';
import { ApiError } from '@/lib/fetcher';
import { batonListKey, fetchBatons } from '@/lib/queries';
import {
  formatBytes,
  formatDurationShort,
  statusBadgeVariant,
} from '@/lib/baton-format';

export function Batons() {
  const navigate = useNavigate();
  const [page, setPage] = React.useState({ limit: 20, offset: 0 });
  const q = useQuery({
    queryKey: batonListKey(page),
    queryFn: () => fetchBatons(page),
  });

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Batons</h1>
          <p className="text-muted-foreground">
            Prepaid handoff, collaboration, and binding-record batons.
          </p>
        </div>
        <Button asChild>
          <Link to="/dashboard/batons/new">
            <Plus className="h-4 w-4" /> Create baton
          </Link>
        </Button>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Your batons</CardTitle>
        </CardHeader>
        <CardContent>
          {q.isPending ? (
            <div className="space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : q.error ? (
            <Alert variant="destructive">
              <AlertTitle>
                Couldn't load batons (
                {q.error instanceof ApiError ? q.error.code : 'unknown'})
              </AlertTitle>
              <AlertDescription>
                <Button
                  size="sm"
                  variant="outline"
                  className="mt-2"
                  onClick={() => q.refetch()}
                >
                  Retry
                </Button>
              </AlertDescription>
            </Alert>
          ) : q.data && q.data.data.length === 0 ? (
            <EmptyState
              icon={Boxes}
              title="No batons yet"
              description="Create your first baton — pick a preset, price it, add content, and provision."
              action={
                <Button asChild>
                  <Link to="/dashboard/batons/new">
                    <Plus className="h-4 w-4" /> Create baton
                  </Link>
                </Button>
              }
            />
          ) : q.data ? (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Baton</TableHead>
                    <TableHead>Preset · Tier</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Size left</TableHead>
                    <TableHead>Expires in</TableHead>
                    <TableHead className="text-right">Price</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {q.data.data.map((b) => (
                    <TableRow
                      key={b.id}
                      className="cursor-pointer"
                      onClick={() =>
                        navigate(`/dashboard/batons/${encodeURIComponent(b.id)}`)
                      }
                    >
                      <TableCell>
                        <Link
                          to={`/dashboard/batons/${encodeURIComponent(b.id)}`}
                          className="font-medium hover:underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {b.name ?? b.id}
                        </Link>
                        {b.name ? (
                          <div className="font-mono text-xs text-muted-foreground">
                            {b.id}
                          </div>
                        ) : null}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        <span className="capitalize">{b.preset}</span>
                        <span className="text-muted-foreground"> · {b.tier}</span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          <Badge variant={statusBadgeVariant(b.status)}>
                            {b.status}
                          </Badge>
                          <BatonWarnings warnings={b.warnings} compact />
                        </div>
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-muted-foreground">
                        {formatBytes(b.calculated.bytesRemaining)}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-muted-foreground">
                        {formatDurationShort(b.calculated.secondsRemaining)}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        <MoneyAmount micros={b.priceMicros} minorDigits={4} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="mt-3">
                <Pagination
                  total={q.data.total}
                  limit={page.limit}
                  offset={page.offset}
                  onChange={setPage}
                />
              </div>
            </>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
